import React from 'react';
import { Link } from 'react-router';
import LanguageSwitcher from '../LanguageSwitcher';

interface AuthShellProps {
  children: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '4xl';
}

const maxWidthClass: Record<NonNullable<AuthShellProps['maxWidth']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '4xl': 'max-w-4xl',
};

// Dark deck-aligned shell for every auth screen. Mirrors the public site's
// PublicLayout chrome at a glance — black background, subtle sky glow,
// CallCov logo top-left, language switcher top-right — but without the
// marketing nav, so the user's attention stays on the form.
const AuthShell: React.FC<AuthShellProps> = ({
  children,
  title,
  subtitle,
  maxWidth = 'md',
}) => (
  <div className="min-h-screen bg-black text-white flex flex-col relative overflow-hidden">
    {/* Subtle radial sky glow at the top, matching the marketing hero. */}
    <div
      className="absolute inset-0 pointer-events-none"
      aria-hidden="true"
      style={{
        background:
          'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.08), transparent 70%)',
      }}
    />

    {/* Top bar: brand mark + locale switcher. Same -translate-y trick as the
        public nav so the wordmark sits visually centered with the
        LanguageSwitcher next to it. */}
    <header className="relative z-10 flex items-center justify-between px-6 lg:px-8 py-6">
      <Link
        to="/"
        className="inline-flex items-center -m-1.5 p-1.5"
        aria-label="CallCov, inicio"
      >
        <img
          src="/logos/callcov-white.svg"
          alt="CallCov"
          className="h-12 w-auto -translate-y-[9px]"
        />
      </Link>
      <LanguageSwitcher />
    </header>

    {/* Form area. Centered both axes; bottom padding leaves room above the
        viewport bottom on short screens. */}
    <main className="relative z-10 flex-1 flex items-start justify-center px-6 lg:px-8 pt-8 pb-24">
      <div className={`w-full ${maxWidthClass[maxWidth]}`}>
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-light text-white leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-4 text-base text-white/60">{subtitle}</p>
          )}
        </div>
        {children}
      </div>
    </main>
  </div>
);

export default AuthShell;
